import { compareNaturalNames } from "../../../components/tagOrder";

export const EDGE_TAG_CATALOG_SCHEMA = "controlseat.edge.tag-catalog";
export const EDGE_TAG_CATALOG_VERSION = 1;

const FLOAT_CODECS = new Set(["float32", "float64"]);
const WRITABLE_TABLES = new Set(["coils", "holding_registers"]);
const TRUNCATION_REASONS = new Set(["entry_limit", "payload_limit"]);
const clean = (value) => String(value || "").trim();
const asArray = (value) => (Array.isArray(value) ? value : []);
const sha256 = (value) => /^[a-f0-9]{64}$/.test(String(value || ""));
const count = (value) => Number.isInteger(Number(value)) && Number(value) >= 0
  ? Number(value)
  : null;

export function edgeTagRequirement(mapping = {}) {
  const table = clean(mapping.table);
  if (table === "coils" || table === "discrete_inputs") {
    return { dataType: "bool", writable: WRITABLE_TABLES.has(table) };
  }
  if (table === "holding_registers" || table === "input_registers") {
    return {
      dataType: FLOAT_CODECS.has(mapping.valueType) ? "float64" : "int64",
      writable: WRITABLE_TABLES.has(table),
    };
  }
  return { dataType: "", writable: false };
}

export function normalizeEdgeTagCatalog(payload = {}) {
  const runtime = payload.runtime && typeof payload.runtime === "object" ? payload.runtime : {};
  const raw = payload.catalog && typeof payload.catalog === "object" ? payload.catalog : null;
  const rawPage = payload.page && typeof payload.page === "object" ? payload.page : {};
  const connection = runtime.connection === "online" ? "online" : "offline";
  const catalogFresh = runtime.catalog_fresh === true && connection === "online";
  const base = {
    connection,
    lastSeenAt: runtime.last_seen_at || "",
    catalogFresh,
    snapshotError: clean(runtime.snapshot_error),
    state: connection !== "online" ? "stale" : (!raw ? "unavailable" : (catalogFresh ? "unavailable" : "stale")),
    complete: false,
    truncated: false,
    authoritativeAbsence: false,
    totalCount: 0,
    returnedCount: 0,
    omittedCount: 0,
    catalogSha256: "",
    payloadSha256: "",
    generatedAt: "",
    truncationReasons: [],
    diagnostic: "",
    page: { offset: 0, limit: 0, returnedCount: 0, filteredCount: 0, complete: false },
    inspection: { requestedCount: 0, matchedCount: 0, absentPaths: [], unreportedPaths: [] },
    entries: [],
  };
  if (!raw) return base;

  const entries = asArray(raw.entries).map((entry) => ({
    path: clean(entry?.path),
    dataType: clean(entry?.data_type),
    writable: entry?.writable === true,
    cloudPresent: entry?.cloud_present === true,
    cloudDataType: clean(entry?.cloud_data_type),
    cloudWritable: entry?.cloud_writable === true,
  }));
  const totalCount = count(raw.total_count);
  const returnedCount = count(raw.returned_count);
  const omittedCount = count(raw.omitted_count);
  const offset = count(rawPage.offset) ?? 0;
  const limit = count(rawPage.limit) ?? entries.length;
  const pageReturnedCount = count(rawPage.returned_count) ?? entries.length;
  const filteredCount = count(rawPage.filtered_count) ?? returnedCount;
  const complete = raw.complete === true;
  const truncationReasons = asArray(raw.truncation_reasons).map(clean).filter(Boolean);
  const rawInspection = payload.inspection && typeof payload.inspection === "object" ? payload.inspection : null;
  const inspection = rawInspection ? {
    requestedCount: count(rawInspection.requested_count),
    matchedCount: count(rawInspection.matched_count),
    absentPaths: asArray(rawInspection.absent_paths).map(clean).filter(Boolean),
    unreportedPaths: asArray(rawInspection.unreported_paths).map(clean).filter(Boolean),
  } : null;
  const uniquePaths = new Set(entries.map((entry) => entry.path));
  const uniqueReasons = new Set(truncationReasons);
  const inspectedPaths = inspection ? [
    ...entries.map((entry) => entry.path),
    ...inspection.absentPaths,
    ...inspection.unreportedPaths,
  ] : [];
  const validInspection = !inspection || (
    inspection.requestedCount !== null && inspection.matchedCount === entries.length &&
    inspection.requestedCount === entries.length + inspection.absentPaths.length + inspection.unreportedPaths.length &&
    new Set(inspectedPaths).size === inspectedPaths.length
  );
  const structurallyValid =
    raw.schema === EDGE_TAG_CATALOG_SCHEMA && raw.version === EDGE_TAG_CATALOG_VERSION &&
    sha256(raw.catalog_sha256) &&
    sha256(raw.payload_sha256) &&
    !Number.isNaN(Date.parse(raw.generated_at)) &&
    totalCount !== null && returnedCount !== null && omittedCount !== null &&
    totalCount === returnedCount + omittedCount &&
    pageReturnedCount === entries.length &&
    offset <= filteredCount && offset + pageReturnedCount <= filteredCount &&
    uniquePaths.size === entries.length &&
    entries.every((entry) => entry.path && ["bool", "int64", "float64", "string", "bytes", "json"].includes(entry.dataType)) &&
    truncationReasons.length <= 2 && uniqueReasons.size === truncationReasons.length &&
    truncationReasons.every((reason, index) => TRUNCATION_REASONS.has(reason) && (index === 0 || truncationReasons[index - 1] < reason)) &&
    validInspection &&
    ((complete && omittedCount === 0 && truncationReasons.length === 0 && raw.catalog_sha256 === raw.payload_sha256) ||
      (!complete && omittedCount > 0 && truncationReasons.length > 0));
  if (!structurallyValid) {
    return {
      ...base,
      state: catalogFresh ? "unavailable" : "stale",
      diagnostic: "Runtime tag catalog evidence is malformed.",
    };
  }
  const pageComplete = offset === 0 && pageReturnedCount === filteredCount;
  return {
    ...base,
    state: !catalogFresh ? "stale" : (complete ? "fresh" : "truncated"),
    complete,
    truncated: !complete,
    authoritativeAbsence: catalogFresh && complete && pageComplete,
    totalCount,
    returnedCount,
    omittedCount,
    catalogSha256: String(raw.catalog_sha256).toLowerCase(),
    payloadSha256: String(raw.payload_sha256).toLowerCase(),
    generatedAt: raw.generated_at || "",
    truncationReasons,
    diagnostic: clean(raw.diagnostic),
    page: {
      offset,
      limit,
      returnedCount: pageReturnedCount,
      filteredCount,
      complete: pageComplete,
    },
    inspection: inspection ? {
      requestedCount: inspection.requestedCount,
      matchedCount: inspection.matchedCount,
      absentPaths: inspection.absentPaths,
      unreportedPaths: inspection.unreportedPaths,
    } : base.inspection,
    entries,
  };
}

export function edgeTagCatalogPresentation(catalog) {
  switch (catalog?.state) {
    case "fresh":
      return { label: `Fresh · ${catalog.returnedCount.toLocaleString()} tags`, tone: "isPositive", message: "The selected runtime reported a complete current tag catalog." };
    case "truncated":
      return { label: `Truncated · ${catalog.returnedCount.toLocaleString()} of ${catalog.totalCount.toLocaleString()}`, tone: "isWarning", message: "Only reported paths can be proven available. A missing path is unreported, not absent." };
    case "stale":
      return { label: "Stale", tone: "isWarning", message: catalog?.snapshotError || "The runtime tag catalog is stale or the runtime is offline." };
    default:
      return { label: "Unavailable", tone: "isNeutral", message: catalog?.diagnostic || catalog?.snapshotError || "This runtime has not reported usable tag-catalog evidence." };
  }
}

export function classifyEdgeTag(catalog, path, requirement) {
  const requestedPath = clean(path);
  if (!requestedPath) return { eligible: false, code: "path_required", message: "Tag path is required." };
  if (!catalog || catalog.state === "unavailable") return { eligible: false, code: "unavailable", message: catalog?.diagnostic || catalog?.snapshotError || "Runtime tag catalog is unavailable." };
  if (catalog.state === "stale") return { eligible: false, code: "stale", message: "Runtime tag evidence is stale; wait for a fresh heartbeat." };
  const entry = catalog.entries.find((candidate) => candidate.path === requestedPath);
  if (!entry) {
    if (catalog.inspection?.absentPaths.includes(requestedPath)) {
      return { eligible: false, code: "absent", message: "This path is not provisioned on the selected runtime." };
    }
    if (catalog.inspection?.unreportedPaths.includes(requestedPath)) {
      return { eligible: false, code: "unreported", message: "This path is not in the runtime's reported catalog; its availability is unknown." };
    }
    return catalog.authoritativeAbsence
      ? { eligible: false, code: "absent", message: "This path is not provisioned on the selected runtime." }
      : { eligible: false, code: "unreported", message: "This path is not in the runtime's reported result; its availability is unknown." };
  }
  if (!entry.cloudPresent) {
    return { eligible: false, code: "cloud_absent", entry, message: "This runtime tag is not present in the Control Seat tag catalog." };
  }
  const expected = requirement || { dataType: "", writable: false };
  if (expected.dataType && entry.dataType !== expected.dataType) {
    return { eligible: false, code: "runtime_type", entry, message: `Runtime type is ${entry.dataType}; this mapping requires ${expected.dataType}.` };
  }
  if (expected.dataType && entry.cloudDataType !== expected.dataType) {
    return { eligible: false, code: "cloud_type", entry, message: `Control Seat type is ${entry.cloudDataType || "unknown"}; this mapping requires ${expected.dataType}.` };
  }
  if (expected.writable && !entry.writable) {
    return { eligible: false, code: "runtime_read_only", entry, message: "This mapping writes, but the runtime tag is read only." };
  }
  if (expected.writable && !entry.cloudWritable) {
    return { eligible: false, code: "cloud_read_only", entry, message: "This mapping writes, but the Control Seat tag is read only." };
  }
  return { eligible: true, code: "eligible", entry, message: "Available on the selected runtime with the required scalar type and access." };
}

export function eligibleEdgeTagEntries(catalog, mapping, query = "", limit = 500) {
  const requirement = edgeTagRequirement(mapping);
  const search = clean(query).toLowerCase();
  const out = [];
  for (const entry of asArray(catalog?.entries)) {
    if (search && !entry.path.toLowerCase().includes(search)) continue;
    const result = classifyEdgeTag(catalog, entry.path, requirement);
    if (!result.eligible) continue;
    out.push(entry);
  }
  return out
    .sort((left, right) => compareNaturalNames(left.path, right.path))
    .slice(0, limit);
}

export function currentEdgeTagEvidence(catalog, evidence) {
  if (!catalog?.catalogFresh) return catalog;
  if (!evidence?.catalogFresh) return catalog;
  if (!catalog.catalogSha256 || evidence.catalogSha256 !== catalog.catalogSha256) return catalog;
  return evidence;
}

export function edgeTagCatalogReview(form, catalog, evidenceByPath = {}) {
  if (form?.enabled !== true) return { valid: true, results: [], message: "" };
  const mappings = asArray(form?.mappings);
  const results = mappings.map((mapping) => {
    const path = clean(mapping.tagPath);
    return classifyEdgeTag(currentEdgeTagEvidence(catalog, evidenceByPath[path]), path, edgeTagRequirement(mapping));
  });
  const firstFailure = results.find((result) => !result.eligible);
  return {
    valid: mappings.length > 0 && !firstFailure,
    results,
    message: firstFailure?.message || (mappings.length === 0 ? "A running edge listener requires at least one proven runtime tag mapping." : ""),
  };
}

export function inspectEdgeTags(catalog, { paths = [], query = "", dataType = "", writable = false, limit = 100 } = {}) {
  const requirement = { dataType: clean(dataType), writable: writable === true };
  const boundedLimit = Math.min(100, Math.max(1, Number(limit) || 50));
  if (asArray(paths).length > 0) {
    return asArray(paths).slice(0, 100).map((path) => ({ path: clean(path), ...classifyEdgeTag(catalog, path, requirement) }));
  }
  const search = clean(query).toLowerCase();
  const results = [];
  for (const entry of asArray(catalog?.entries)) {
    if (search && !entry.path.toLowerCase().includes(search)) continue;
    const classification = classifyEdgeTag(catalog, entry.path, requirement);
    results.push({ path: entry.path, ...classification });
    if (results.length >= boundedLimit) break;
  }
  return results;
}
